import { errorToast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import type { ConfirmButtonProps } from "@/v2-backoffice-client/components/button"
import { useLayerReviewRequeueMutation } from "@/v2-backoffice-client/layer/review/requeue/layer.review.requeue.mutation"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"

export type LayerReviewRequeueButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  layers: LayerFragment[]
  onDone?(): void
}

/**
 * Un-rejects one or more layers: wipes the review stamp (and note) so they
 * return to the pending-review queue. Does not touch IsEnabled, so a rejected
 * layer stays hidden from customers until it is published again.
 */
function LayerReviewRequeueButton(props: LayerReviewRequeueButtonProps) {
  const { layers, onDone, children, ...buttonProps } = props

  const [requeue, { loading, error }] = useLayerReviewRequeueMutation({
    refetchQueries: [LAYER_COUNTS_QUERY_NAME],
  })
  errorToast(error, {
    title: "Failed to move layer back to the review queue",
  })

  const count = layers.length

  return (
    <ConfirmButton
      variant="outline"
      size="sm"
      iconSrc="rotate-left"
      isLoading={loading}
      confirmContent={
        count === 1
          ? "Move back to the review queue? The review stamp and note are cleared."
          : `Move ${count} layers back to the review queue? Their review stamps and notes are cleared.`
      }
      content="Clears the review decision so the layer shows up in the review queue again"
      onConfirm={async () => {
        await requeue({
          variables: {
            featureCodes: layers.map((layer) => layer.FeatureCode),
          },
        })
        onDone?.()
      }}
      {...buttonProps}
    >
      {children ?? "Back to queue"}
    </ConfirmButton>
  )
}

export default LayerReviewRequeueButton
